/* istanbul ignore file */
import React, {
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Platform, ViewProps } from 'react-native';
import { ThemeContext } from 'styled-components/native';

import { TextTypes } from 'common/constants/enums';
import { FillAbsolute } from 'components/Containers';
import { LoginFooter } from 'components/Footer/LoginFooter';
import { LoadingOverlay } from 'components/LoadingOverlay/LoadingOverlay';
import { useScreenOrientation } from 'components/ProvidersContainer/ScreenOrientationProvider';
import { useTransformContentStyleVertical } from 'components/ResponsiveLayout/hooks/useGetDynamicContentStyles';
import { NHSWebViewModal } from 'components/Screens/NHSWebViewModal';
import { HelperText } from 'components/Text/HelperText';
import {
  ButtonColorSchemes,
  UniversalButton,
} from 'components/UniveralButtons';
import {
  CREATE_ACCOUNT_CALLBACK,
  NHS_ONBOARDING_PATH,
} from 'config/deepLinking';
import { PLATGEN_ONBOARDING_URL } from 'config/envVars';
import { ResizeMode } from 'cross-platform/Image';
import { useHistory, useParams } from 'cross-platform/react-router';
import { roles } from 'cross-platform/utils/roleProps';
import { useTransformContentStyle } from 'daylight/hooks';
import {
  NHS_ENABLED_FLAG,
  useFeatureFlags,
} from 'lib/api/hooks/useFeatureFlags';
import { LoginPhases } from 'lib/api/hooks/useLogin';
import { useBigHealthLogin } from 'lib/api/hooks/useLogin/useBigHealthLogin';
import { openNHSCreateAccountBrowser } from 'lib/navigation/openNHSCreateAccountBrowser';
import { buildOnboardingUrl } from 'lib/navigation/openOnboardingBrowser';
import {
  CREATE_ACCOUNT_CANCELLED_MESSAGE,
  CREATE_ACCOUNT_ERROR_MESSAGE,
} from 'lib/strings';

import {
  BackgroundImage,
  ButtonContainer,
  FormattedText,
  LimitWidth,
  LoginSplashScreenScroll,
  Logo,
  LogoContainer,
  NHSButton,
  NHSButtonCell,
  NHSButtonRow,
  NHSButtonSection,
  NHSErrorContainer,
  TextContainer,
} from './LoginSplashScreen.styled';

type ProductParams = {
  productReference: string;
};

export const LoginSplashScreen = ({
  byLine,
  noAccountOptional,
  noAccount,
  website,
  logoAsset,
  splashScreenAsset,
  overrides,
  optionalFooter,
  nhsCreateAccountButton,
  nhsLoginButton,
}: {
  byLine: string;
  noAccountOptional?: string;
  noAccount: string;
  website: string;
  // TODO remove optional modifier
  // HOW create platform-specific types for getAssetsForProduct
  // WHEN have plenty fo time and care about types
  logoAsset?: string;
  splashScreenAsset?: string;
  /* "overrides" are short fixes, which will be removed once theming is fixed */
  overrides?: {
    SignInButton?: {
      colorScheme?: ButtonColorSchemes;
    };
    FillAbsolute?: ViewProps;
  };
  optionalFooter?: boolean;
  nhsCreateAccountButton?: string;
  nhsLoginButton?: string;
}): ReactElement => {
  const { status } = useBigHealthLogin();
  const {
    getFeatureFlags,
    featureFlags,
    flagsRequested,
    loading: flagsLoading,
  } = useFeatureFlags();

  const transformStyle = useTransformContentStyle();
  const transformStyleVertical = useTransformContentStyleVertical();
  const { productReference } = useParams<ProductParams>();
  const theme = useContext(ThemeContext);
  const history = useHistory();
  const locationState = history.location.state as {
    nhsError?: 'cancelled' | 'error';
    androidOnboardingUrl?: string;
  } | null;
  const nhsErrorFromHistory = locationState?.nhsError;
  const androidOnboardingUrlFromHistory = locationState?.androidOnboardingUrl;
  const [nhsError, setNhsError] = useState(nhsErrorFromHistory);
  const [showBrowser, setShowBrowser] = useState(false);
  const [onboardingUrl, setOnboardingUrl] = useState<string | null>(null);
  const createAccountUrl = useMemo(
    () =>
      // Use the configured onboarding origin verbatim (must match the Auth0 redirect_uri).
      `${PLATGEN_ONBOARDING_URL}${NHS_ONBOARDING_PATH}?redirect_url=${encodeURIComponent(
        CREATE_ACCOUNT_CALLBACK
      )}`,
    []
  );

  // setNhsError is needed for Path 1: component stays mounted when history.replace fires on the same route, so useState won't reinitialize
  useEffect(() => {
    if (nhsErrorFromHistory) {
      setNhsError(nhsErrorFromHistory);
      history.replace(`/${productReference}/login`);
    }
  }, [nhsErrorFromHistory, history, productReference]);

  useEffect(() => {
    if (androidOnboardingUrlFromHistory) {
      setOnboardingUrl(buildOnboardingUrl(androidOnboardingUrlFromHistory));
      history.replace(`/${productReference}/login`);
    }
  }, [androidOnboardingUrlFromHistory, history, productReference]);

  const screenOrientation = useScreenOrientation();
  const isPortrait =
    screenOrientation === 'PORTRAIT' ||
    screenOrientation === 'PORTRAIT-UPSIDEDOWN';

  useEffect(() => {
    if (!flagsRequested) {
      getFeatureFlags();
    }
  }, [flagsRequested, getFeatureFlags]);

  // Gate on nhsCreateAccountButton (Sleepio-only) so Daylight is unaffected even if the API returns this flag truthy.
  const isNHSEnabled =
    !!nhsCreateAccountButton && Boolean(featureFlags?.[NHS_ENABLED_FLAG]);

  const handleSubmit = (): void => {
    if (isNHSEnabled) {
      history.push(`/${productReference}/nhs-login/access-selector`);
      return;
    }
    history.push(`/${productReference}/login-screen`);
  };

  const handleCreateAccount = async (): Promise<void> => {
    if (Platform.OS === 'android') {
      setShowBrowser(true);
      return;
    }
    const {
      deepLinkReceived,
      isError,
      token,
    } = await openNHSCreateAccountBrowser();
    if (isError) {
      setNhsError('error');
    } else if (!deepLinkReceived) {
      setNhsError('cancelled');
    } else if (!token) {
      // Deep link arrived with status=success but no handoff token — surface an
      // error instead of silently dropping the user back on the login form.
      setNhsError('error');
    }
    // When token is present, DeepLinking.native.tsx already navigated to
    // create-account-callback via _parseAndPush when the URL event fired.
  };

  const transformWidth = website.includes('Sleepio') ? 180 : 120;

  return (
    <FillAbsolute {...(overrides?.FillAbsolute || null)}>
      {Platform.OS === 'android' && (
        <>
          <NHSWebViewModal
            visible={showBrowser}
            url={createAccountUrl}
            callbackUrl={CREATE_ACCOUNT_CALLBACK}
            onSuccess={path => {
              setShowBrowser(false);
              if (!path.includes('status=success')) {
                setNhsError('error');
                return;
              }
              const token = new URLSearchParams(path.split('?')[1] ?? '').get(
                'token'
              );
              if (token) {
                // Delegate the handoff to the dedicated controller.
                history.push(
                  `/${productReference}/create-account-callback?status=success&token=${encodeURIComponent(
                    token
                  )}`
                );
              } else {
                setNhsError('error');
              }
            }}
            onCancel={() => {
              setShowBrowser(false);
              setNhsError('cancelled');
            }}
            onError={() => {
              setShowBrowser(false);
              setNhsError('error');
            }}
          />
          {onboardingUrl ? (
            <NHSWebViewModal
              visible={true}
              url={onboardingUrl}
              callbackUrl={CREATE_ACCOUNT_CALLBACK}
              onSuccess={path => {
                setOnboardingUrl(null);
                history.replace(`/${productReference}/${path}`);
              }}
              onCancel={() => setOnboardingUrl(null)}
              onError={() => setOnboardingUrl(null)}
            />
          ) : null}
        </>
      )}
      <LoginSplashScreenScroll bounces={false}>
        {splashScreenAsset !== undefined && (
          <BackgroundImage
            {...roles('BackgroundImageSplashScreen')}
            src={splashScreenAsset}
            alt={`${productReference} Splash Logo`}
          />
        )}
        <LimitWidth>
          <LogoContainer>
            <Logo
              src={logoAsset}
              fit={ResizeMode.contain}
              style={{
                width: transformStyle(transformWidth),
                height: 120,
                alignSelf: 'flex-end',
              }}
              alt={`${productReference} Logo`}
            />
          </LogoContainer>
          <TextContainer
            style={{
              paddingTop: transformStyleVertical(32),
              height: isPortrait ? '45%' : 'auto',
            }}
          >
            <FormattedText
              // TODO: use the theme
              // WHEN alignment with designers is found
              style={{
                fontSize: transformStyle(24),
                color: theme.color.text.splashPage,
              }}
            >
              {byLine}
            </FormattedText>
            {nhsError ? (
              <NHSErrorContainer>
                <HelperText presentationType={TextTypes.ERROR}>
                  {nhsError === 'error'
                    ? CREATE_ACCOUNT_ERROR_MESSAGE
                    : CREATE_ACCOUNT_CANCELLED_MESSAGE}
                </HelperText>
              </NHSErrorContainer>
            ) : null}
          </TextContainer>
          {!nhsCreateAccountButton || (flagsRequested && !flagsLoading) ? (
            isNHSEnabled ? (
              <NHSButtonSection>
                <NHSButtonRow>
                  <NHSButtonCell>
                    <NHSButton
                      {...roles('create-account-button')}
                      onPress={handleCreateAccount}
                      isDisabled={false}
                      text={nhsCreateAccountButton ?? 'Create account'}
                      adjustsFontSizeToFit
                      colorScheme={
                        overrides?.SignInButton?.colorScheme ||
                        ButtonColorSchemes.Secondary
                      }
                    />
                  </NHSButtonCell>
                  <NHSButtonCell>
                    <NHSButton
                      {...roles('SignIn-button')}
                      onPress={handleSubmit}
                      isDisabled={false}
                      text={nhsLoginButton ?? 'Login'}
                      colorScheme={
                        overrides?.SignInButton?.colorScheme ||
                        ButtonColorSchemes.Secondary
                      }
                    />
                  </NHSButtonCell>
                </NHSButtonRow>
                <FormattedText
                  style={{
                    fontSize: transformStyle(16),
                    color: theme.color.text.splashPage,
                    fontWeight: '500',
                    paddingTop: 16,
                  }}
                >
                  {noAccount}
                </FormattedText>
                {optionalFooter ? <LoginFooter /> : null}
              </NHSButtonSection>
            ) : (
              <ButtonContainer>
                <TextContainer
                  style={{
                    paddingTop: transformStyleVertical(0),
                    height: 'auto',
                  }}
                >
                  {noAccountOptional ? (
                    <FormattedText
                      style={{
                        fontSize: transformStyle(18),
                        color: theme.color.text.splashPage,
                        width: '100%',
                        paddingBottom: 16,
                      }}
                    >
                      {noAccountOptional}
                    </FormattedText>
                  ) : null}
                  <FormattedText
                    style={{
                      fontSize: 20,
                      fontWeight: '500',
                      color: theme.color.text.splashPage,
                      width: '100%',
                      paddingBottom: 16,
                    }}
                  >
                    Don&apos;t have an account?
                  </FormattedText>
                  <FormattedText
                    style={{
                      fontSize: transformStyle(16),
                      color: theme.color.text.splashPage,
                      fontWeight: '500',
                      paddingBottom: 32,
                    }}
                  >
                    {noAccount}
                  </FormattedText>
                  <UniversalButton
                    {...roles('SignIn-button')}
                    onPress={handleSubmit}
                    isDisabled={false}
                    text={'Sign in'}
                    colorScheme={
                      overrides?.SignInButton?.colorScheme ||
                      ButtonColorSchemes.Secondary
                    }
                  />
                  {optionalFooter ? <LoginFooter /> : null}
                </TextContainer>
              </ButtonContainer>
            )
          ) : (
            <LoadingOverlay />
          )}
          {status === LoginPhases.FETCHING ? <LoadingOverlay /> : null}
        </LimitWidth>
      </LoginSplashScreenScroll>
    </FillAbsolute>
  );
};

export default LoginSplashScreen;
